import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { authService } from '../../services/auth';
import './AdminLayout.css';
import { useTranslation } from 'react-i18next';

interface AdminLayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  titleKey: string;
  icon: string;
  path?: string;
  groupKey?: string;
  children?: NavItem[];
  exact?: boolean;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<string[]>(['content-management']);
  const user = authService.getCurrentUser();

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path);
  };

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups(prev => 
      prev.includes(groupKey) 
        ? prev.filter(key => key !== groupKey)
        : [...prev, groupKey]
    );
  };

  const isGroupExpanded = (groupKey: string) => {
    return expandedGroups.includes(groupKey);
  };

  const navItems: NavItem[] = [
    {
      titleKey: 'nav.dashboard',
      icon: 'fas fa-tachometer-alt',
      path: '/admin',
      exact: true
    },
    {
      titleKey: 'nav.contentManagement',
      icon: 'fas fa-cogs',
      groupKey: 'content-management',
      children: [
        {
          titleKey: 'nav.cmsOverview',
          icon: 'fas fa-th-large',
          path: '/admin/cms'
        },
        {
          titleKey: 'nav.pages',
          icon: 'fas fa-file-alt',
          path: '/admin/pages'
        },
        {
          titleKey: 'nav.notices',
          icon: 'fas fa-bullhorn',
          path: '/admin/notices'
        },
        {
          titleKey: 'nav.videoGallery',
          icon: 'fas fa-video',
          path: '/admin/videos'
        },
        {
          titleKey: 'nav.imageGallery',
          icon: 'fas fa-images',
          path: '/admin/image-galleries'
        }
      ]
    },
    {
      titleKey: 'nav.buildingManagement',
      icon: 'fas fa-building',
      groupKey: 'building-management',
      children: [
        {
          titleKey: 'nav.buildings',
          icon: 'fas fa-building',
          path: '/admin/buildings'
        },
        {
          titleKey: 'nav.owners',
          icon: 'fas fa-users',
          path: '/admin/owners'
        },
        {
          titleKey: 'nav.assignOwner',
          icon: 'fas fa-user-plus',
          path: '/admin/owner-assignment'
        }
      ]
    },
    {
      titleKey: 'nav.userManagement',
      icon: 'fas fa-users-cog',
      groupKey: 'user-management',
      children: [
        {
          titleKey: 'nav.allUsers',
          icon: 'fas fa-users',
          path: '/admin/users'
        }
      ]
    },
    {
      titleKey: 'nav.mediaManagement',
      icon: 'fas fa-images',
      groupKey: 'media-management',
      children: [
        {
          titleKey: 'nav.mediaLibrary',
          icon: 'fas fa-images',
          path: '/admin/media'
        },
        {
          titleKey: 'nav.uploadFiles',
          icon: 'fas fa-upload',
          path: '/admin/media/upload'
        }
      ]
    },
    {
      titleKey: 'nav.systemTools',
      icon: 'fas fa-tools',
      groupKey: 'system-tools',
      children: [
        {
          titleKey: 'nav.artisanCommands',
          icon: 'fas fa-terminal',
          path: '/admin/artisan'
        }
      ]
    }
  ];

  const renderNavItem = (item: NavItem, level = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = hasChildren && isGroupExpanded(item.groupKey!);
    const isItemActive = item.path ? isActive(item.path) : false;
    const title = t(item.titleKey);

    if (hasChildren) {
      return (
        <div key={item.titleKey} className="nav-group">
          <button
            className={`nav-group-header ${isExpanded ? 'expanded' : ''}`}
            onClick={() => toggleGroup(item.groupKey!)}
          >
            <i className={`${item.icon} nav-icon`}></i>
            {!sidebarCollapsed && <span className="nav-text">{title}</span>}
            {!sidebarCollapsed && (
              <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} nav-arrow`}></i>
            )}
          </button>
          {isExpanded && !sidebarCollapsed && (
            <div className="nav-group-content">
              {item.children!.map((child) => renderNavItem(child, level + 1))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.titleKey}
        to={item.path!}
        className={`nav-item ${isItemActive ? 'active' : ''}`}
        style={{ paddingLeft: `${12 + level * 20}px` }}
      >
        <i className={`${item.icon} nav-icon`}></i>
        {!sidebarCollapsed && <span className="nav-text">{title}</span>}
      </Link>
    );
  };

  return (
    <div className="admin-layout">
      {/* Sidebar */}
      <div className={`admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <div className="logo">
            <img 
              src="/logo.png" 
              alt={t('app.name')} 
              style={{
                height: '40px',
                width: 'auto',
                objectFit: 'contain',
                background: 'white',
                padding: '6px',
                borderRadius: '6px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}
            />
            {!sidebarCollapsed && <span className="logo-text">{t('app.name')}</span>}
          </div>
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          >
            <i className={`fas fa-${sidebarCollapsed ? 'chevron-right' : 'chevron-left'}`}></i>
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map(item => renderNavItem(item))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            {!sidebarCollapsed && (
              <div className="user-details">
                <div className="user-name">{user?.name || 'Admin'}</div>
                <div className="user-role">{user?.role || 'Administrator'}</div>
              </div>
            )}
          </div>
          <button
            className="logout-btn"
            onClick={() => {
              authService.logout();
              window.location.href = '/login';
            }}
          >
            <i className="fas fa-sign-out-alt"></i>
            {!sidebarCollapsed && <span>{t('nav.logout')}</span>}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="admin-main">
        <header className="admin-header">
          <div className="header-left">
            <h1>{t('admin.panelTitle')}</h1>
          </div>
          <div className="header-right">
            <div className="language-switcher">
              <label htmlFor="language-select">{t('language.switchLabel')}</label>
              <select
                id="language-select"
                value={i18n.language || 'en'}
                onChange={(event) => i18n.changeLanguage(event.target.value)}
              >
                <option value="en">{t('language.english')}</option>
                <option value="bn">{t('language.bangla')}</option>
              </select>
            </div>
            <div className="header-actions">
              <button className="action-btn" title={t('admin.notifications')}>
                <i className="fas fa-bell"></i>
                <span className="badge">3</span>
              </button>
              <button className="action-btn" title={t('admin.settings')}>
                <i className="fas fa-cog"></i>
              </button>
            </div>
          </div>
        </header>

        <main className="admin-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;